import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import type { UserRole } from '../store/useAuthStore';

/**
 * Admin query hooks — wrap the tenancy + identity + audit Lambda surfaces.
 * Every hook hits the real API; pages render loading/empty/error uniformly
 * through `ErrorState` + `LoadingState` primitives.
 */

export interface AdminTenant {
  id: string;
  name: string;
  slug?: string;
  status: 'active' | 'trial' | 'suspended';
  users: number;
  ranges: number;
  spendCap: number;
  monthToDate: number;
  scenariosEnabled?: boolean;
  licenseValid?: boolean;
  licenseReason?: string;
  licenseStartedAt?: string | null;
  licenseExpiresAt?: string | null;
  isPlatform?: boolean;
  /** Trainee-facing experience template chosen by superadmin ('default' | 'fortress'). */
  template?: 'default' | 'fortress';
}

export interface AdminUser {
  id: string;
  email: string;
  role: UserRole;
  mfaEnabled: boolean;
  lastSeen: string;
  status: 'active' | 'invited' | 'disabled';
  tenantId?: string;
}

export interface AuditRow {
  ts: string;
  actor: string;
  tenantId: string;
  action: string;
  resource: string;
  correlationId: string;
  outcome: 'allow' | 'deny';
}

export const adminKeys = {
  tenants: () => ['admin', 'tenants'] as const,
  users: () => ['admin', 'users'] as const,
  audit: () => ['admin', 'audit'] as const,
};

function unwrapItems<T>(resp: unknown): ReadonlyArray<T> {
  if (Array.isArray(resp)) return resp as ReadonlyArray<T>;
  const items = (resp as { items?: ReadonlyArray<T> })?.items;
  return items ?? [];
}

export function useTenants() {
  return useQuery<ReadonlyArray<AdminTenant>>({
    queryKey: adminKeys.tenants(),
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<unknown>('/tenants', { method: 'GET', signal });
      return unwrapItems<AdminTenant>(resp);
    },
  });
}

export function useUsers(tenantId?: string) {
  return useQuery<ReadonlyArray<AdminUser>>({
    queryKey: [...adminKeys.users(), tenantId ?? 'all'],
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<unknown>(
        tenantId ? `/users?tenant_id=${encodeURIComponent(tenantId)}` : '/users',
        { method: 'GET', signal },
      );
      return unwrapItems<AdminUser>(resp);
    },
  });
}

export function useTenant(id: string | undefined) {
  return useQuery<AdminTenant>({
    queryKey: [...adminKeys.tenants(), id ?? ''],
    enabled: Boolean(id),
    queryFn: async ({ signal }) =>
      apiFetch<AdminTenant>('/tenants/{tenantId}', { method: 'GET', pathParams: { tenantId: id ?? '' }, signal }),
  });
}

export function useAuditTrail() {
  return useQuery<ReadonlyArray<AuditRow>>({
    queryKey: adminKeys.audit(),
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<unknown>('/audit/events', { method: 'GET', signal });
      return unwrapItems<AuditRow>(resp);
    },
  });
}

export interface CreateTenantInput {
  name: string;
  adminEmail: string;
  spendCap?: number;
  licenseMonths?: number;
  scenariosEnabled?: boolean;
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation<AdminTenant, Error, CreateTenantInput>({
    mutationFn: async (input) =>
      apiFetch<AdminTenant>('/tenants', {
        method: 'POST',
        body: {
          name: input.name,
          admin_email: input.adminEmail,
          ...(input.spendCap !== undefined ? { spend_cap: input.spendCap } : {}),
          ...(input.licenseMonths !== undefined ? { license_months: input.licenseMonths } : {}),
          scenarios_enabled: input.scenariosEnabled ?? true,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.tenants() });
    },
  });
}

export function useDeleteTenant() {
  const qc = useQueryClient();
  return useMutation<void, Error, { tenantId: string }>({
    mutationFn: async ({ tenantId }) => {
      await apiFetch<void>('/tenants/{tenantId}', {
        method: 'DELETE',
        pathParams: { tenantId },
        body: { confirm: 'DELETE' },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.tenants() });
    },
  });
}

export interface InviteUserInput {
  email: string;
  role: UserRole;
  tenantId?: string;
  firstName?: string;
  lastName?: string;
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation<AdminUser, Error, InviteUserInput>({
    mutationFn: async (input) =>
      apiFetch<AdminUser>('/users/invite', {
        method: 'POST',
        body: {
          email: input.email,
          role: input.role,
          ...(input.tenantId !== undefined ? { tenant_id: input.tenantId } : {}),
          ...(input.firstName !== undefined ? { first_name: input.firstName } : {}),
          ...(input.lastName !== undefined ? { last_name: input.lastName } : {}),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useDisableUser() {
  const qc = useQueryClient();
  return useMutation<void, Error, { userId: string }>({
    mutationFn: async ({ userId }) => {
      await apiFetch<void>('/users/{userId}/disable', {
        method: 'POST',
        pathParams: { userId },
        body: {},
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

/** Re-enable a previously disabled user (reverses useDisableUser). */
export function useEnableUser() {
  const qc = useQueryClient();
  return useMutation<void, Error, { userId: string }>({
    mutationFn: async ({ userId }) => {
      await apiFetch<void>('/users/{userId}/enable', {
        method: 'POST',
        pathParams: { userId },
        body: {},
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

/**
 * Permanently delete a user (DELETE /users/{userId}). Destructive and
 * irreversible — removes the Cognito identity so they can no longer sign in and
 * the email is freed for a fresh invite. The UI confirms before calling this.
 * Backend enforces the same rank/tenant guard as disable.
 */
export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation<void, Error, { userId: string }>({
    mutationFn: async ({ userId }) => {
      await apiFetch<void>('/users/{userId}', {
        method: 'DELETE',
        pathParams: { userId },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export interface SetLicenseInput {
  tenantId: string;
  licenseMonths?: number;
  licenseExpiresAt?: string;
  scenariosEnabled: boolean;
}

export function useSetLicense() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, SetLicenseInput>({
    mutationFn: async (input) =>
      apiFetch('/tenants/{tenantId}/license', {
        method: 'POST',
        pathParams: { tenantId: input.tenantId },
        body: {
          scenarios_enabled: input.scenariosEnabled,
          ...(input.licenseMonths !== undefined ? { license_months: input.licenseMonths } : {}),
          ...(input.licenseExpiresAt ? { license_expires_at: input.licenseExpiresAt } : {}),
        },
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: adminKeys.tenants() }); },
  });
}

/** The caller's own org entitlement (scenarios + license validity). Superadmins
 * are never gated. Non-admins read their single org from the tenants list. */
export function useEntitlements(): { scenariosAllowed: boolean; licenseValid: boolean; reason: string | undefined; loading: boolean } {
  const tenants = useTenants();
  const org = tenants.data?.[0];
  const licenseValid = org?.licenseValid ?? true;         // grandfathered/unknown -> allow
  const scenariosAllowed = licenseValid && (org?.scenariosEnabled ?? true);
  return { scenariosAllowed, licenseValid, reason: org?.licenseReason, loading: tenants.isPending };
}
